import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-bold border-2 border-foreground transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 active:translate-x-1 active:translate-y-1 active:shadow-none',
  {
    variants: {
      variant: {
        default:     'bg-primary text-primary-foreground shadow-neo hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg',
        destructive: 'bg-destructive text-destructive-foreground shadow-neo hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg',
        outline:     'bg-background text-foreground shadow-neo hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg',
        secondary:   'bg-secondary text-secondary-foreground shadow-neo hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-neo-lg',
        ghost:       'border-transparent text-foreground shadow-none hover:bg-muted hover:border-foreground hover:shadow-neo-sm',
        link:        'border-transparent shadow-none text-foreground underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-10 px-5 py-2',
        sm:      'h-8 px-3 text-xs',
        lg:      'h-12 px-8 text-base',
        icon:    'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
